// src/components/TermsAndConditions.tsx
import React from "react";

const sections = [
  { id: "about-crushhh", label: "About Crushhh" },
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "definitions", label: "Definitions" },
  { id: "eligibility", label: "Eligibility & Registration" },
  { id: "verification", label: "Age Verification & Eligibility Review" },
  { id: "tiers", label: "Free Tier & Paid Tier" },
  { id: "license", label: "Limited License" },
  { id: "conduct", label: "Prohibited Conduct" },
  { id: "communications", label: "Communications" },
  { id: "purchases", label: "Purchases & Refunds" },
  { id: "termination", label: "Suspension & Termination" },
  { id: "privacy", label: "Privacy & Data Handling" },
  { id: "third-party", label: "Third-Party Services" },
  { id: "ip", label: "Intellectual Property" },
  { id: "disclaimer", label: "Disclaimer of Warranties" },
  { id: "liability", label: "Limitation of Liability" },
  { id: "indemnity", label: "Indemnification" },
  { id: "disputes", label: "Dispute Resolution" },
  { id: "changes", label: "Changes to Terms" },
  { id: "misc", label: "Miscellaneous" },
  { id: "contact", label: "Contact Information" },
];

const headingStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: "1.1rem",
  marginTop: "2.5rem",
  marginBottom: "1rem",
  color: "#222",
  borderBottom: "1px solid #eee",
  paddingBottom: "0.25rem",
  letterSpacing: ".01em",
};

const linkStyle: React.CSSProperties = {
  color: "#0077cc",
  textDecoration: "underline",
  fontWeight: 600,
};

const TermsAndConditions: React.FC = () => (
  <div
    style={{
      maxWidth: 900,
      margin: "auto",
      padding: "2.5rem 1rem",
      background: "#fff",
      color: "#222",
      fontFamily: "Segoe UI, Arial, sans-serif",
      lineHeight: 1.65,
    }}
  >
    <h1
      style={{
        textAlign: "center",
        marginBottom: "1.5rem",
        fontWeight: 800,
        fontSize: "1.5rem",
      }}
    >
      Terms &amp; Conditions of Use – Crushhh.com
    </h1>

    <p style={{ textAlign: "center", fontStyle: "italic", marginBottom: "2rem" }}>
      Effective Date: May 21, 2026
    </p>

    <nav
      style={{
        margin: "2rem 0 2.5rem 0",
        borderBottom: "1px solid #eee",
        paddingBottom: "1rem",
      }}
    >
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          justifyContent: "center",
        }}
      >
        {sections.map((s) => (
          <li key={s.id}>
            <a href={`#${s.id}`} style={{ ...linkStyle, fontSize: "1rem" }}>
              <b>{s.label}</b>
            </a>
          </li>
        ))}
      </ul>
    </nav>

    <section id="about-crushhh">
      <h2 style={headingStyle}><b>About Crushhh</b></h2>
      <ul>
        <li>
          Crushhh is a relationship-oriented service designed for adults who already
          know, have met, or are otherwise aware of one another and who want a more
          private way to indicate interest and, where applicable, learn whether such
          interest is mutual.
        </li>
        <li>
          Crushhh is not a guarantee of introductions, responses, compatibility,
          communication, romance, or relationships. It is a technology service that
          provides certain features subject to eligibility, verification, feature
          limits, account standing, and these Terms.
        </li>
        <li>
          Crushhh may offer a free tier and one or more paid features or paid tiers.
          Feature availability may depend on age verification, account verification,
          successful payment, continued eligibility, and compliance with these Terms.
        </li>
      </ul>
    </section>

    <section id="acceptance">
      <h2 style={headingStyle}><b>Acceptance of Terms</b></h2>
      <ul>
        <li>
          These Terms &amp; Conditions of Use (“Terms”) are a legally binding agreement
          between you and Crushhh Inc. (“Crushhh,” “we,” “us,” or “our”).
        </li>
        <li>
          By accessing or using the Crushhh website, web application, mobile
          application, or any related content, features, functionality, communications,
          or services (collectively, the “Service”), you agree to be bound by these
          Terms.
        </li>
        <li>
          If you do not agree to these Terms, you must not access or use the Service.
        </li>
        <li>
          Your use of the Service is also subject to our{" "}
          <a href="/privacy" style={linkStyle}>Privacy Policy</a>,{" "}
          <a href="/cookies" style={linkStyle}>Cookie Policy</a>, and any additional
          rules, guidelines, safety notices, promotional terms, or feature-specific
          terms we make available, all of which are incorporated into these Terms by
          reference.
        </li>
      </ul>
    </section>

    <section id="definitions">
      <h2 style={headingStyle}><b>Definitions</b></h2>
      <ul>
        <li><b>“Account”</b> means a registered user account for the Service.</li>
        <li><b>“Content”</b> means text, graphics, images, audio, video, software, code, trademarks, branding, data, and other materials made available through the Service.</li>
        <li><b>“Free Tier”</b> means any no-charge level of access offered by Crushhh.</li>
        <li><b>“Paid Tier”</b> means any paid feature, upgrade, unlock, product, membership, or premium level offered by Crushhh.</li>
        <li><b>“Verification”</b> means any process used by Crushhh or its service providers to assess age, identity, ownership of contact points, account integrity, fraud risk, or eligibility.</li>
        <li><b>“Service”</b> has the meaning set forth above.</li>
      </ul>
    </section>

    <section id="eligibility">
      <h2 style={headingStyle}><b>Eligibility &amp; Registration</b></h2>
      <ul>
        <li>
          The Service is intended only for residents of the United States, unless and
          until Crushhh expressly makes the Service available elsewhere.
        </li>
        <li>
          You must be at least eighteen (18) years old and legally capable of entering
          into a binding agreement to use the Service.
        </li>
        <li>
          You may use only one Account unless we expressly authorize otherwise in
          writing.
        </li>
        <li>
          You must provide accurate, complete, and current information and must update
          your information as needed to keep it accurate.
        </li>
        <li>
          You may not create or use an Account if:
          <ul>
            <li>you are prohibited by law from using the Service;</li>
            <li>you are using the Service for a commercial, unlawful, abusive, or fraudulent purpose;</li>
            <li>you were previously suspended or removed from the Service, unless we expressly allow your return in writing;</li>
            <li>you provide false, misleading, deceptive, or incomplete registration or verification information; or</li>
            <li>you are acting on behalf of another person or entity without authorization.</li>
          </ul>
        </li>
        <li>
          You are solely responsible for maintaining the confidentiality of your login
          credentials and for all activity occurring under your Account.
        </li>
      </ul>
    </section>

    <section id="verification">
      <h2 style={headingStyle}><b>Age Verification &amp; Eligibility Review</b></h2>
      <ul>
        <li>
          Age verification is required by Crushhh as a condition of access to the
          Service and/or as a condition of access to specific features, as determined
          by Crushhh in its discretion.
        </li>
        <li>
          Crushhh may also require additional verification steps, including verification
          of your email address, phone number, identity, ownership of a contact point,
          payment method, or other information relevant to account security, fraud
          prevention, abuse prevention, legal compliance, or feature eligibility.
        </li>
        <li>
          You authorize Crushhh and its service providers to conduct such verification
          processes as we deem reasonably necessary.
        </li>
        <li>
          If you fail, refuse, abandon, or do not pass any required verification or
          review process, we may deny registration, restrict features, refuse upgrades,
          suspend access, terminate your Account, or take any other action we deem
          appropriate.
        </li>
        <li>
          Verification may be required again at any time if we reasonably determine
          that re-verification is necessary.
        </li>
        <li>
          Completion of a verification flow does not create a right to approval,
          continued access, or continued eligibility.
        </li>
      </ul>
    </section>

    <section id="tiers">
      <h2 style={headingStyle}><b>Free Tier &amp; Paid Tier</b></h2>
      <ul>
        <li>
          Crushhh may make certain features available at no charge through a Free Tier.
          Free Tier features may be limited, modified, suspended, or discontinued at
          any time.
        </li>
        <li>
          Crushhh may also offer Paid Tier features, upgrades, unlocks, or memberships.
          The features, limitations, duration, scope, and price of any Paid Tier offer
          will be described at or before the point of purchase.
        </li>
        <li>
          We reserve the right to change, add, remove, repackage, price, reprice,
          restrict, or discontinue any tier, feature, benefit, or limitation at any
          time, subject to applicable law.
        </li>
        <li>
          Access to particular features may depend on verification, account standing,
          technical availability, payment completion, geographic availability, and
          compliance with these Terms.
        </li>
        <li>
          Crushhh does not guarantee any match, relationship, interaction, disclosure,
          communication, or outcome through either the Free Tier or the Paid Tier.
        </li>
      </ul>
    </section>

    <section id="license">
      <h2 style={headingStyle}><b>Limited License</b></h2>
      <ul>
        <li>
          Subject to your compliance with these Terms, Crushhh grants you a limited,
          revocable, non-exclusive, non-transferable, non-sublicensable license to
          access and use the Service for personal, non-commercial use only.
        </li>
        <li>
          No rights are granted except as expressly stated in these Terms.
        </li>
        <li>
          You may not copy, reproduce, distribute, publicly display, modify, create
          derivative works from, reverse engineer, decompile, disassemble, or exploit
          any portion of the Service except as expressly permitted by us in writing or
          by non-waivable law.
        </li>
      </ul>
    </section>

    <section id="conduct">
      <h2 style={headingStyle}><b>Prohibited Conduct</b></h2>
      <ul>
        <li>You may not use the Service for any unlawful, harmful, harassing, abusive, fraudulent, deceptive, or unauthorized purpose.</li>
        <li>You may not stalk, threaten, intimidate, shame, embarrass, exploit, extort, or otherwise harm another person.</li>
        <li>You may not impersonate another person or misrepresent your age, identity, eligibility, or intentions.</li>
        <li>You may not use automation, bots, scraping tools, scripts, or similar means to access, monitor, extract, or interact with the Service.</li>
        <li>You may not attempt to infer non-public information about another user through repeated submissions, account cycling, testing, probing, or similar manipulative conduct.</li>
        <li>You may not interfere with or disrupt the Service, its infrastructure, or related systems, including by introducing malware, bypassing restrictions, or attempting unauthorized access.</li>
        <li>You may not use the Service to advertise, solicit, spam, collect leads, or conduct commercial activity.</li>
        <li>You may not use the Service in a way that violates another person’s rights, applicable law, court order, or safety interests.</li>
      </ul>
    </section>

    <section id="communications">
      <h2 style={headingStyle}><b>Communications</b></h2>
      <ul>
        <li>
          By creating an Account or using the Service, you consent to receive
          communications from Crushhh relating to your Account, verification, security,
          transactions, feature access, service updates, and other Service-related matters.
        </li>
        <li>
          Where offered, you may separately opt in to receive SMS/text messages and
          push notifications. Message frequency may vary.
        </li>
        <li>
          You are responsible for any carrier charges, data charges, device charges,
          or connectivity costs associated with your use of the Service.
        </li>
        <li>
          Delivery of emails, texts, push notifications, and similar communications
          depends on third-party systems and technical conditions outside our control.
          We do not guarantee successful, timely, or continuous delivery.
        </li>
        <li>
          You may opt out of non-essential promotional communications where legally
          required and where opt-out functionality is provided. Certain transactional,
          account, verification, fraud-prevention, legal, or security communications
          may still be sent.
        </li>
      </ul>
    </section>

    <section id="purchases">
      <h2 style={headingStyle}><b>Purchases &amp; Refunds</b></h2>
      <ul>
        <li>
          Free Tier access, if offered, does not require payment. Charges apply only if
          and when you elect to purchase a Paid Tier feature or other paid offering.
        </li>
        <li>
          By making a purchase, you authorize Crushhh and its payment processors to
          charge the amount displayed at checkout, together with any applicable taxes,
          fees, and charges.
        </li>
        <li>
          Unless expressly stated otherwise at the point of purchase, purchases are
          one-time, non-recurring transactions.
        </li>
        <li>
          Because the Service involves digital access, digital features, compliance
          screening, fraud-prevention measures, and other immediately consumed or
          allocated resources, all sales are final and non-refundable except as
          required by applicable law or as expressly stated by Crushhh in writing.
        </li>
        <li>
          To the fullest extent permitted by law, verification-related costs,
          processing costs, compliance costs, and anti-fraud costs are non-refundable.
        </li>
        <li>
          If you do not complete verification, do not satisfy eligibility requirements,
          violate these Terms, initiate abusive or fraudulent payment disputes, or are
          otherwise denied access for cause, Crushhh may refuse, revoke, or limit paid
          access without refund except where required by law.
        </li>
        <li>
          If you believe you are legally entitled to a refund, you must contact{" "}
          <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>{" "}
          and provide sufficient information for review.
        </li>
        <li>
          Nothing in these Terms limits any non-waivable consumer rights that may apply
          to you under applicable law.
        </li>
      </ul>
    </section>

    <section id="termination">
      <h2 style={headingStyle}><b>Suspension &amp; Termination</b></h2>
      <ul>
        <li>
          You may stop using the Service at any time. If account deletion is made
          available, you may use the deletion function or contact us to request deletion.
        </li>
        <li>
          Crushhh may investigate, restrict, suspend, disable, or terminate your
          Account or access to the Service at any time, with or without notice, if we
          believe that you have violated these Terms, failed verification, become
          ineligible, created risk for other users, exposed Crushhh to legal or
          operational risk, or otherwise misused the Service.
        </li>
        <li>
          We may also deny future re-registration or access.
        </li>
        <li>
          Suspension or termination does not waive or eliminate any rights or remedies
          available to Crushhh.
        </li>
        <li>
          Where permitted by law, no refund or credit will be issued for suspensions
          or terminations based on your violation of these Terms, failed verification,
          or ineligibility.
        </li>
      </ul>
    </section>

    <section id="privacy">
      <h2 style={headingStyle}><b>Privacy &amp; Data Handling</b></h2>
      <ul>
        <li>
          Your use of the Service is subject to our{" "}
          <a href="/privacy" style={linkStyle}>Privacy Policy</a>.
        </li>
        <li>
          You acknowledge that Crushhh may collect, use, store, process, disclose, and
          retain personal information as described in the Privacy Policy and as
          otherwise permitted or required by law.
        </li>
        <li>
          We may retain information for legal compliance, fraud prevention, abuse
          prevention, dispute resolution, security, enforcement, recordkeeping, and
          other legitimate business purposes, subject to applicable law.
        </li>
      </ul>
    </section>

    <section id="third-party">
      <h2 style={headingStyle}><b>Third-Party Services</b></h2>
      <ul>
        <li>
          The Service may rely on third-party providers, including hosting providers,
          payment processors, communications providers, analytics providers,
          verification providers, cloud service providers, and similar vendors.
        </li>
        <li>
          We do not control third-party services and are not responsible for their
          acts, omissions, availability, downtime, security, content, or policies.
        </li>
        <li>
          Your use of third-party services may also be governed by the third party’s
          own terms and privacy policy.
        </li>
      </ul>
    </section>

    <section id="ip">
      <h2 style={headingStyle}><b>Intellectual Property</b></h2>
      <ul>
        <li>
          The Service and all associated Content, software, trademarks, service marks,
          logos, branding, designs, layouts, interfaces, compilations, and other
          intellectual property are owned by or licensed to Crushhh and are protected
          by applicable law.
        </li>
        <li>
          Except for the limited license granted above, no right, title, or interest
          in or to the Service or any Content is transferred to you.
        </li>
      </ul>
    </section>

    <section id="disclaimer">
      <h2 style={headingStyle}><b>Disclaimer of Warranties</b></h2>
      <ul>
        <li>
          TO THE FULLEST EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED “AS IS” AND
          “AS AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED,
          STATUTORY, OR OTHERWISE.
        </li>
        <li>
          TO THE FULLEST EXTENT PERMITTED BY LAW, CRUSHHH DISCLAIMS ALL IMPLIED
          WARRANTIES, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, AND QUIET ENJOYMENT.
        </li>
        <li>
          CRUSHHH DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE,
          SECURE, AVAILABLE AT ALL TIMES, OR FREE OF DEFECTS OR HARMFUL COMPONENTS.
        </li>
        <li>
          CRUSHHH DOES NOT WARRANT ANY PARTICULAR RESULT, MATCH, RESPONSE,
          COMMUNICATION, DISCLOSURE, TIMING OUTCOME, OR RELATIONSHIP OUTCOME.
        </li>
        <li>
          CRUSHHH DOES NOT WARRANT THE IDENTITY, INTENT, TRUTHFULNESS, LEGITIMACY,
          CONDUCT, OR COMPATIBILITY OF ANY USER OR THIRD PARTY.
        </li>
      </ul>
    </section>

    <section id="liability">
      <h2 style={headingStyle}><b>Limitation of Liability</b></h2>
      <ul>
        <li>
          TO THE FULLEST EXTENT PERMITTED BY LAW, CRUSHHH INC. AND ITS AFFILIATES,
          OFFICERS, DIRECTORS, EMPLOYEES, CONTRACTORS, LICENSORS, AND SERVICE
          PROVIDERS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL,
          SPECIAL, EXEMPLARY, PUNITIVE, OR ENHANCED DAMAGES, OR FOR ANY LOSS OF
          PROFITS, REVENUE, DATA, GOODWILL, BUSINESS OPPORTUNITY, OR OTHER INTANGIBLE
          LOSSES, ARISING OUT OF OR RELATING TO THE SERVICE.
        </li>
        <li>
          TO THE FULLEST EXTENT PERMITTED BY LAW, CRUSHHH SHALL NOT BE LIABLE FOR THE
          ACTS, OMISSIONS, STATEMENTS, CONTENT, CONDUCT, OR OFFLINE BEHAVIOR OF ANY
          USER OR THIRD PARTY.
        </li>
        <li>
          TO THE FULLEST EXTENT PERMITTED BY LAW, CRUSHHH’S TOTAL AGGREGATE LIABILITY
          TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SERVICE SHALL NOT
          EXCEED THE GREATER OF: (A) THE TOTAL AMOUNT YOU PAID TO CRUSHHH IN THE
          TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM; OR (B)
          USD $100.
        </li>
        <li>
          THESE LIMITATIONS APPLY REGARDLESS OF THE LEGAL THEORY ASSERTED AND EVEN IF
          ANY LIMITED REMEDY FAILS OF ITS ESSENTIAL PURPOSE.
        </li>
        <li>
          IF APPLICABLE LAW DOES NOT ALLOW CERTAIN LIMITATIONS, THIS SECTION APPLIES
          ONLY TO THE MAXIMUM EXTENT PERMITTED BY LAW.
        </li>
      </ul>
    </section>

    <section id="indemnity">
      <h2 style={headingStyle}><b>Indemnification</b></h2>
      <p>
        To the fullest extent permitted by law, you agree to indemnify, defend, and
        hold harmless Crushhh Inc., its affiliates, officers, directors, employees,
        contractors, licensors, and service providers from and against any claims,
        actions, liabilities, damages, judgments, awards, losses, costs, and expenses,
        including reasonable attorneys’ fees, arising out of or relating to your use
        of the Service, your violation of these Terms, your violation of applicable
        law, or your infringement or violation of any rights of another person or entity.
      </p>
    </section>

    <section id="disputes">
      <h2 style={headingStyle}><b>Dispute Resolution</b></h2>
      <ul>
        <li>
          Before initiating any formal dispute process, you agree to first contact
          Crushhh at{" "}
          <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>{" "}
          and attempt to resolve the matter informally.
        </li>
        <li>
          Except where prohibited by law, any dispute, claim, or controversy arising
          out of or relating to these Terms or the Service shall be resolved by
          binding individual arbitration rather than in court, except that either
          party may pursue an eligible matter in small claims court.
        </li>
        <li>
          To the fullest extent permitted by law, disputes must be brought only on an
          individual basis and not as part of any class, collective, coordinated,
          consolidated, or representative proceeding.
        </li>
        <li>
          Arbitration shall be administered by the American Arbitration Association (AAA) in accordance with
          its applicable rules, except as modified by these Terms.
        </li>
        <li>
          You may opt out of arbitration by sending written notice to{" "}
          <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>{" "}
          within thirty (30) days of your first acceptance of these Terms. Your notice
          must include your full name, account email, and an unequivocal statement
          that you wish to opt out of arbitration.
        </li>
        <li>
          These Terms are governed by the laws of the State of Wyoming, without
          regard to conflict of laws principles, except to the extent superseded by
          applicable federal law or otherwise required by law.
        </li>
      </ul>
    </section>

    <section id="changes">
      <h2 style={headingStyle}><b>Changes to Terms</b></h2>
      <p>
        We may modify these Terms at any time. Updated Terms will be posted with a
        revised effective date. Where required by law, we may also provide additional
        notice. Your continued use of the Service after revised Terms become effective
        constitutes your acceptance of the revised Terms.
      </p>
    </section>

    <section id="misc">
      <h2 style={headingStyle}><b>Miscellaneous</b></h2>
      <ul>
        <li>
          <b>Entire Agreement:</b> These Terms, together with any Incorporated Policies,
          constitute the entire agreement between you and Crushhh regarding the Service.
        </li>
        <li>
          <b>Severability:</b> If any provision of these Terms is found unenforceable,
          the remaining provisions will remain in full force and effect to the maximum
          extent permitted by law.
        </li>
        <li>
          <b>No Waiver:</b> A failure by Crushhh to enforce any provision of these Terms
          is not a waiver of that provision or any other provision.
        </li>
        <li>
          <b>Assignment:</b> You may not assign or transfer these Terms or any rights
          under them without our prior written consent. We may assign these Terms and
          our rights and obligations under them without restriction, subject to applicable law.
        </li>
      </ul>
    </section>

    <section id="contact">
      <h2 style={headingStyle}><b>Contact Information</b></h2>
      <ul>
        <li>
          <b>Email:</b>{" "}
          <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>
        </li>
        <li>
          <b>Business Address:</b> CRUSHHH INC, 5830 E 2ND ST, STE 7000, CASPER, WY 82609
        </li>
      </ul>
      <p>
        For more information regarding how we handle personal information, please see
        our <a href="/privacy" style={linkStyle}>Privacy Policy</a>.
      </p>
    </section>
  </div>
);

export default TermsAndConditions;